/**
 * CWR 2.2 / 2.4 — service catalog card (Services tab). Markup/structure copied
 * from the confirmed mockup. Every service can be opened, regardless of its
 * schedule or the business's session-booking pause.
 */
import type { KeyboardEvent } from 'react';
import type { HubServiceDto } from '@/api/types';
import { FocalImage } from '@/components/FocalImage';
import { modalityLabel, servicePriceParts } from './catalogFormat';
import { ClockIcon, ArrowIcon, modalityIcon } from './catalogIcons';
import styles from './BookSessionPage.module.css';

const IMAGE_FALLBACK = 'linear-gradient(135deg, var(--bg-card-2), var(--bg-card))';

export interface ServiceCatalogCardProps {
  service: HubServiceDto;
  /** Navigate to the service detail page. */
  onView?: (service: HubServiceDto) => void;
}

export function ServiceCatalogCard({ service, onView }: ServiceCatalogCardProps) {
  const price = servicePriceParts(service.pricing);
  const interactive = !!onView;
  const activate = () => interactive && onView!(service);

  return (
    <article
      className={styles.serviceCard}
      data-testid={`service-catalog-card-${service.id}`}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `View service: ${service.name}`,
            onClick: activate,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            },
          }
        : {})}
    >
      {service.imageUrl ? (
        <FocalImage
          src={service.imageUrl}
          alt=""
          className={styles.serviceImage}
          focalPointX={service.focalPointX}
          focalPointY={service.focalPointY}
          focalPointScale={service.focalPointScale}
          loading="lazy"
        />
      ) : (
        <div className={styles.serviceImage} style={{ backgroundImage: IMAGE_FALLBACK }} />
      )}
      <div className={styles.serviceContent}>
        <h2 className={styles.serviceTitle}>{service.name}</h2>
        {service.summary && <p className={styles.serviceDesc}>{service.summary}</p>}

        <div className={styles.serviceMeta}>
          <div className={styles.metaLine}>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={`${styles.serviceMetaItem} ${styles.modality}`}>
                {modalityIcon(service.modality)}
                {modalityLabel(service.modality)}
              </span>
            </span>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={styles.serviceMetaItem}>
                {ClockIcon}
                {service.duration} min
              </span>
            </span>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={`${styles.serviceMetaPrice} ${price.free ? styles.free : ''}`}>
                {price.free ? (
                  'Free'
                ) : (
                  <>
                    {price.fromPrefix && <span className={styles.from}>from</span>}
                    {price.amount}
                  </>
                )}
              </span>
            </span>
          </div>
        </div>

        <span className={styles.serviceCta}>
          View Service
          {ArrowIcon}
        </span>
      </div>
    </article>
  );
}
